/**
 * Реальные заведения на Resstro, единый источник для плашки в hero, бегущей строки
 * и карты. Стартуем со снимка (src/data/establishments-snapshot.json): он попадает
 * в пререндер и в первый кадр без «прыжка» цифр; после монтирования один раз
 * подтягиваем живые данные из API и подменяем.
 */
import { useEffect, useState } from 'react';
import { getEstablishmentsMap, type MapPoint } from './publicApi';
import snapshot from '@/data/establishments-snapshot.json';

export interface VenueSummary {
  id: string;
  name: string;
  city: string;
  logoUrl: string | null;
  address: string | null;
}

export interface Venues {
  total: number;
  /** Города по убыванию количества заведений. */
  cities: string[];
  venues: VenueSummary[];
  /** true, данные пришли из API, false, из снимка сборки. */
  live: boolean;
}

interface SnapshotPoint {
  city: string;
  count: number;
  establishments: Array<Omit<VenueSummary, 'city'>>;
}

// Тестовые и заглушечные записи не показываем в витрине (на карте они остаются -
// карта честно показывает всё, что отдаёт API).
const HIDDEN_NAME = /test|тест|demo|демо/i;

function fromSnapshot(points: SnapshotPoint[]): Venues {
  return summarize(
    points.map((p) => ({ city: p.city, count: p.count, establishments: p.establishments })),
    false,
  );
}

function summarize(
  points: Array<{ city: string; count: number; establishments: Array<Omit<VenueSummary, 'city'>> }>,
  live: boolean,
): Venues {
  const sorted = [...points].sort((a, b) => b.count - a.count);
  const venues: VenueSummary[] = [];
  for (const p of sorted) {
    for (const e of p.establishments) {
      venues.push({ id: e.id, name: e.name.trim(), city: p.city, logoUrl: e.logoUrl, address: e.address });
    }
  }
  return {
    total: points.reduce((s, p) => s + p.count, 0),
    cities: sorted.map((p) => p.city),
    venues,
    live,
  };
}

export const SNAPSHOT_VENUES: Venues = fromSnapshot((snapshot as { points: SnapshotPoint[] }).points);

/** Название для витрины: без служебного префикса «Заведение», без двойных пробелов. */
export function displayName(name: string): string {
  return name.replace(/^заведение\s+/i, '').replace(/\s+/g, ' ').trim();
}

/** Заведения, пригодные для публичной витрины (без тестовых), с уникальными именами. */
export function showcaseVenues(v: Venues): VenueSummary[] {
  const seen = new Set<string>();
  const out: VenueSummary[] = [];
  for (const e of v.venues) {
    const name = displayName(e.name);
    const key = name.toLowerCase();
    if (!name || HIDDEN_NAME.test(name) || seen.has(key)) continue;
    seen.add(key);
    out.push({ ...e, name });
  }
  return out;
}

// Запрос к API, один на страницу, сколько бы компонентов ни подписалось.
let pointsPromise: Promise<MapPoint[]> | null = null;
export function loadEstablishments(): Promise<MapPoint[]> {
  if (!pointsPromise) {
    pointsPromise = getEstablishmentsMap()
      .then((res) => (Array.isArray(res.points) ? res.points : []))
      .catch((e: unknown) => {
        pointsPromise = null; // следующая подписка попробует ещё раз
        throw e;
      });
  }
  return pointsPromise;
}

/** Сводка по заведениям: снимок сразу, живые данные, после загрузки. */
export function useEstablishments(): Venues {
  const [venues, setVenues] = useState<Venues>(SNAPSHOT_VENUES);

  useEffect(() => {
    let cancelled = false;
    loadEstablishments()
      .then((points) => {
        if (cancelled || points.length === 0) return;
        setVenues(summarize(points, true));
      })
      .catch(() => {
        /* остаёмся на снимке */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return venues;
}
